import {CurrencyPipe, DecimalPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, signal,} from '@angular/core';
import {Observable, tap} from 'rxjs';
import type {PropertyFactsResponse, SaveUnitsRequest} from '../../../models/valuation.models';
import {RentControlLabelPipe} from '../../../shared/pipes/rent-control-label.pipe';
import {UnitTypeLabelPipe} from '../../../shared/pipes/unit-type-label.pipe';
import {ValuationTabComponent} from '../valuation-tab.component';
import type {CanComponentDeactivate} from '../../../routing/can-deactivate.guard';
import {computeUnitCode, MANAGER_UNIT_CODE} from './unit-code.util';

interface EditableUnitRow {
  rowId: string;
  bedrooms: number;
  bathrooms: number | null;
  unit_count: number;
  is_manager: boolean;
}

@Component({
  selector: 'app-property-facts-tab',
  imports: [
    CurrencyPipe,
    DecimalPipe,
    RentControlLabelPipe,
    UnitTypeLabelPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './property-facts-tab.component.html',
  styleUrl: './property-facts-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyFactsTabComponent
  extends ValuationTabComponent<PropertyFactsResponse>
  implements CanComponentDeactivate
{
  protected readonly tabTitle = 'Property Facts';

  readonly editedUnits = signal<EditableUnitRow[]>([]);
  readonly unitsError = signal<string | null>(null);

  readonly hasManagerRow = computed(() => this.editedUnits().some((r) => r.is_manager));

  /** True once facts have loaded but no unit data could be resolved (from PRODA,
   * unit_mix, or a prior manual save) -- the appraiser must enter it manually. */
  readonly unitsRequired = computed(() => {
    const f = this.data();
    return f !== null && f.unit_groups.length === 0;
  });

  readonly duplicateCodeError = computed<string | null>(() => {
    const codes = this.editedUnits().map((r) =>
      computeUnitCode(r.bedrooms, r.bathrooms, r.is_manager),
    );
    const seen = new Set<string>();
    for (const code of codes) {
      if (seen.has(code)) {
        return code === MANAGER_UNIT_CODE
          ? 'Only one manager unit type is allowed.'
          : 'Unit types must have unique bedrooms/bathrooms combinations.';
      }
      seen.add(code);
    }
    return null;
  });

  constructor() {
    super();
    this.valid.set(true);

    effect(() => {
      const hasUnits = (this.data()?.unit_groups.length ?? 0) > 0;
      this.conditions.update(c => ({ ...c, hasUnits }))
    });
  }

  protected override loadData(): Observable<PropertyFactsResponse> {
    return this.valuationService.getPropertyFacts(this.sessionId()).pipe(
      tap((facts) => {
        if (facts.unit_groups.length === 0) this.startEditUnits(facts);
      }),
    );
  }

  canDeactivate(): boolean {
    if (!this.editing()) return true;
    return window.confirm('You have unsaved changes on this tab. Leave without saving?');
  }

  startEditUnits(current: PropertyFactsResponse | null = this.data()): void {
    if (!current) return;
    this.unitsError.set(null);
    this.editedUnits.set(
      current.unit_groups.map((u) => ({
        rowId: crypto.randomUUID(),
        bedrooms: u.bedrooms,
        bathrooms: u.bathrooms,
        unit_count: u.unit_count,
        is_manager: u.is_manager,
      })),
    );
    this.editing.set(true);
  }

  cancelEditUnits(): void {
    this.editing.set(false);
    this.editedUnits.set([]);
    this.unitsError.set(null);
  }

  addUnitRow(isManager: boolean): void {
    this.editedUnits.update((rows) => [
      ...rows,
      {
        rowId: crypto.randomUUID(),
        bedrooms: 0,
        bathrooms: null,
        unit_count: 1,
        is_manager: isManager,
      },
    ]);
  }

  removeUnitRow(rowId: string): void {
    this.editedUnits.update((rows) => rows.filter((r) => r.rowId !== rowId));
  }

  updateBedrooms(rowId: string, raw: string): void {
    const parsed = Number(raw);
    const bedrooms = Number.isNaN(parsed) ? 0 : Math.max(0, Math.trunc(parsed));
    this.editedUnits.update((rows) =>
      rows.map((r) => (r.rowId === rowId ? { ...r, bedrooms } : r)),
    );
  }

  updateBathrooms(rowId: string, raw: string): void {
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    const bathrooms = parsed === null || Number.isNaN(parsed) ? null : parsed;
    this.editedUnits.update((rows) =>
      rows.map((r) => (r.rowId === rowId ? { ...r, bathrooms } : r)),
    );
  }

  updateUnitCount(rowId: string, raw: string): void {
    const parsed = Number(raw);
    const unit_count = Number.isNaN(parsed) ? 1 : Math.max(1, Math.trunc(parsed));
    this.editedUnits.update((rows) =>
      rows.map((r) => (r.rowId === rowId ? { ...r, unit_count } : r)),
    );
  }

  async saveUnits(): Promise<void> {
    if (this.duplicateCodeError()) return;
    this.unitsError.set(null);

    const body: SaveUnitsRequest = {
      units: this.editedUnits().map((r) => ({
        bedrooms: r.bedrooms,
        bathrooms: r.bathrooms,
        unit_count: r.unit_count,
        is_manager: r.is_manager,
      })),
    };
    const response = await this.load(
      this.valuationService.saveUnits(this.sessionId(), body),
      { error: 'Failed to save unit types.' }
    )
    if (response) {
      this.setData(response);
      this.editing.set(false);
      this.editedUnits.set([]);
    }
    this.setData(response);
  }
}
